"use client"
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { FiMenu, FiArrowRight, FiX, FiChevronDown } from "react-icons/fi";
import { FaUserCircle } from "react-icons/fa";
import {
    useMotionValueEvent,
    AnimatePresence,
    useScroll,
    motion,
} from "framer-motion";
import useMeasure from "react-use-measure";
import Link from "next/link";
import Image from "next/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from "@/lib/utils";
import { Logo } from "@/components/logo";
import { toast } from "sonner";
import { usePathname } from "next/navigation";
import { ChevronRight } from "lucide-react";
import { ITservices } from "@/app/(landingPage)/home-page/products-services";

export const LandingPageNav = () => {
    const [scrolled, setScrolled] = useState(false);
    const { scrollY } = useScroll();
    const pathname = usePathname();

    const isHomePage = pathname === "/";

    useMotionValueEvent(scrollY, "change", (latest) => {
        if (isHomePage) {
            setScrolled(latest > 50);
        }
    });

    return (
        <nav
            className={`fixed top-0 z-50 w-full px-6 mb-16 transition-all duration-300 ease-out lg:px-12 ${
                isHomePage
                    ? scrolled
                        ? "bg-white text-black"
                        : "bg-transparent text-white"
                    : "bg-white text-black"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between">
                {scrolled || !isHomePage ? (
                    <Logo />
                ) : (
                    <Image
                            src="/footer-logo.svg"
                        alt="Conceev Digital"
                        width={164}
                        height={50}
                        className="py-2 ml-2"
                    />
                )}
                <div className="hidden gap-6 lg:flex">
                    <Links />
                    <CTAs/>
                </div>
                <MobileMenu />
            </div>
        </nav>
    );
};



const Links = () => {
    return (
        <div className="flex items-center gap-6">
            {LINKS.map((l) => (
                <NavLink key={l.text} href={l.href} FlyoutContent={l.component}>
                    {l.text}
                </NavLink>
            ))}
        </div>
    );
};

const NavLink = ({
    children,
    href,
    FlyoutContent,
}: {
    children: React.ReactNode;
    href: string;
    FlyoutContent?: React.ElementType;
}) => {
    const [open, setOpen] = useState(false);

    const showFlyout = FlyoutContent && open;

    return (
        <div
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            className="relative h-fit w-fit"
        >
            <a href={href} className="relative">
                {children}
                <span
                    style={{
                        transform: showFlyout ? "scaleX(1)" : "scaleX(0)",
                    }}
                    className="absolute -bottom-2 -left-2 -right-2 h-1 origin-left scale-x-0 rounded-full bg-[#c40234] transition-transform duration-300 ease-out"
                />
            </a>
            <AnimatePresence>
                {showFlyout && (
                    <motion.div
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 15 }}
                        style={{ translateX: "-50%" }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="absolute left-1/2 top-12 bg-white text-black"
                    >
                        <div className="absolute -top-6 left-0 right-0 h-6 bg-transparent" />
                        <div className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white" />
                        <FlyoutContent />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const AboutUsContent = () => {
    return (
        <div className="w-full bg-red-50 p-6 shadow-none lg:w-[250px] lg:shadow-xl">
            <div className="grid grid-cols-2 lg:grid-cols-1">
                <div className="mb-3 space-y-3">
                    <a href="/terms-and-condition" className="block text-sm hover:underline">
                        Terms and Conditions
                    </a>
                    <a href="/privacy-policy" className="block text-sm hover:underline">
                        Privacy Policy
                    </a>
                    <a href="/refund-policy" className="block text-sm hover:underline">
                        Refund Policy
                    </a>
                    <a href="/disclaimer" className="block text-sm hover:underline">
                        Disclaimer
                    </a>
                </div>
            </div>
        </div>
    );
};


const CTAs = () => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });

    useEffect(() => {
        if (formSubmitted) {
            toast.success("Message sent successfully");
            setFormData({
                name: "",
                email: "",
                subject: "",
                message: "",
            });
            setIsDialogOpen(false);
            setFormSubmitted(false);
        }
    }, [formSubmitted]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const response = await fetch("/api/contact-us", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
            });
            if (response.ok) {
                setFormSubmitted(true);
            } else {
                console.error("Failed to send message");
                toast.error("Failed to send message");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("An error occurred while sending the message");
        }
    };

    return (
        <div className="flex items-center gap-3">
            <Link href="/auth/login" className={`flex items-center bg-white gap-2 rounded-lg border-2 border-black px-4 py-2 font-semibold text-black transition-colors hover:bg-black hover:text-white`}>
                <FaUserCircle />
                <span>Sign in</span>
            </Link>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                    <button
                        className="rounded-lg border-2 border-[#c40234] bg-[#c40234] px-4 py-2 font-semibold text-white transition-colors hover:border-red-600 hover:bg-red-600 hover:text-white"
                    >
                        Schedule a Demo
                    </button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Schedule a Demo</DialogTitle>
                    </DialogHeader>
                    <div>
                        <form onSubmit={handleSubmit} className="rounded px-8 pt-6 pb-8 mb-4">
                            <div className="flex space-x-4 mb-4">
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Name"
                                    required
                                    className="w-1/2 px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#c40233]"
                                />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Email"
                                    required
                                    className="w-1/2 px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#c40233]"
                                />
                            </div>

                            <div className="mb-4">
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    placeholder="Subject"
                                    required
                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#c40233]"
                                />
                            </div>

                            <div className="mb-4">
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Message"
                                    rows={5}
                                    required
                                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#c40233]"
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <button
                                    type="submit"
                                    className={cn(
                                        `relative z-0 flex items-center gap-2 overflow-hidden rounded-md border-[1px] px-8 py-2 text-sm font-bold transition-all duration-300

        before:absolute before:inset-0
        before:-z-10 before:translate-x-[150%]
        before:translate-y-[150%] before:scale-[2.5]
        before:rounded-[100%] before:bg-[#94082B] before:transition-transform
        before:duration-1000 before:content-[""]

        hover:text-white
        hover:before:translate-x-[0%]
        hover:before:translate-y-[0%]
        active:scale-95`
                                    )}
                                >
                                    Let&apos;s Talk
                                </button>
                            </div>
                        </form>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};


const ServicesContent = () => {
    return (
        <div className="grid h-fit w-full grid-cols-12 shadow-xl lg:h-72 lg:w-[800px] lg:shadow-none">
            <div className="col-span-12 flex flex-col justify-between bg-[#c40233] p-6 lg:col-span-4">
                <div>
                    <h2 className="mb-2 text-xl font-semibold text-white">Services</h2>
                    <p className="mb-6 max-w-xs text-sm text-white">
                        Conceev is a CRM software that helps you manage your customers and grow.
                    </p>
                </div>
                <a
                    href="/software-products"
                    className="flex items-center gap-1 text-xs text-white hover:underline"
                >
                    Learn more <FiArrowRight />
                </a>
            </div>
            <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 bg-red-50 p-6 lg:col-span-8">
                <div>
                    <h3 className="font-semibold mb-2">Digital Marketing Services</h3>
                    <ul className="space-y-1">
                        {[
                            { href: "/services/website-design-and-development", text: "Website Design and Development" },
                            { href: "/services/search-engine-optimization", text: "Search Engine Optimization (SEO)" },
                            { href: "/services/ppc", text: "Pay Per Click (PPC) Advertising" },
                            { href: "/services/smm", text: "Social Media Marketing" },
                            { href: "/services/content-marketing", text: "Content Marketing" },
                            { href: "/services/orm", text: "Online Reputation Management" },
                            { href: "/services/email-marketing", text: "Email Marketing" },
                        ].map((service, index) => (
                            <li key={index} className="flex items-center">
                                <ChevronRight className="h-4 w-4 mr-1 text-gray-600" />
                                <a href={service.href} className="hover:underline text-sm">
                                    {service.text}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="font-semibold mb-2">Branding and Graphic Designing</h3>
                    <ul className="space-y-1">
                        {[
                            { href: "/services/logo-designing", text: "Logo Designing" },
                            { href: "/services/brand-guidelines", text: "Brand Guidelines" },
                            { href: "/services/print-media-services", text: "Print Media Services" },
                            { href: "/services/marketing-collaterals", text: "Marketing Collaterals" },
                            { href: "/services/video-shooting-editing", text: "Video Shooting and Editing" },
                        ].map((service, index) => (
                            <li key={index} className="flex items-center">
                                <ChevronRight className="h-4 w-4 mr-1 text-gray-600" />
                                <a href={service.href} className="hover:underline text-sm">
                                    {service.text}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="font-semibold mb-4">IT Services</h3>
                    <ul className="space-y-1">
                        {ITservices.map((service, index) => (
                                <li key={index} className="flex items-center">
                                    <ChevronRight className="h-4 w-4 mr-1 text-gray-600" />
                                    <a href={service.href} className="hover:underline text-sm">
                                        {service.title}
                                    </a>
                                </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};


const ProductsContent = () => {
    return (
        <div className="grid h-fit w-full grid-cols-12 shadow-xl lg:h-72 lg:w-[600px] lg:shadow-none xl:w-[750px]">
            <div className="col-span-12 flex flex-col justify-between bg-[#c40233] p-6 lg:col-span-4">
                <div>
                    <h2 className="mb-2 text-xl font-semibold text-white">Products</h2>
                    <p className="mb-6 max-w-xs text-sm text-white">
                        Conceev is a CRM software that helps you manage your customers and grow
                    </p>
                </div>
                <a
                    href="/software-products"
                    className="flex items-center gap-1 text-xs text-white hover:underline"
                >
                    Learn more <FiArrowRight />
                </a>
            </div>
            <div className="col-span-12 grid grid-cols-2 grid-rows-2 gap-3 bg-red-50 p-6 lg:col-span-8">
                <a
                    href="/software-products/crm"
                    className="rounded border-2 border-neutral-200 bg-white p-3 transition-colors hover:bg-neutral-100"
                >
                    <h3 className="mb-1 font-semibold">CRM</h3>
                    <p className="text-sm">
                        Effortlessly manage customer relationships and drive growth with streamlined tools.
                    </p>
                </a>
                <a
                    href="/software-products/appointments"
                    className="rounded border-2 border-neutral-200 bg-white p-3 transition-colors hover:bg-neutral-100"
                >
                    <h3 className="mb-1 font-semibold">Appointments</h3>
                    <p className="text-sm">
                        Simplify scheduling and stay organized with smart appointment management
                    </p>
                </a>
                <a
                    href="/software-products/social"
                    className="rounded border-2 border-neutral-200 bg-white p-3 transition-colors hover:bg-neutral-100"
                >
                    <h3 className="mb-1 font-semibold">Social</h3>
                    <p className="text-sm">
                        Boost engagement and manage social interactions seamlessly from one place.
                    </p>
                </a>
            </div>
        </div>
    );
};

const MobileMenuLink = ({
    children,
    href,
    FoldContent,
    setMenuOpen,
}: {
    children: React.ReactNode;
    href: string;
    FoldContent?: React.ElementType;
    setMenuOpen: Dispatch<SetStateAction<boolean>>;
}) => {
    const [ref, { height }] = useMeasure();
    const [open, setOpen] = useState(false);

    return (
        <div className="relative text-neutral-950">
            {FoldContent ? (
                <div
                    className="flex w-full cursor-pointer items-center justify-between border-b border-neutral-300 py-6 text-start text-2xl font-semibold"
                    onClick={() => setOpen((pv) => !pv)}
                >
                    <a
                        onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen(false);
                        }}
                        href={href}
                    >
                        {children}
                    </a>
                    <motion.div
                        animate={{ rotate: open ? "180deg" : "0deg" }}
                        transition={{
                            duration: 0.3,
                            ease: "easeOut",
                        }}
                    >
                        <FiChevronDown />
                    </motion.div>
                </div>
            ) : (
                <a
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                    }}
                    href="#"
                    className="flex w-full cursor-pointer items-center justify-between border-b border-neutral-300 py-6 text-start text-2xl font-semibold"
                >
                    <span>{children}</span>
                    <FiArrowRight />
                </a>
            )}
            {FoldContent && (
                <motion.div
                    initial={false}
                    animate={{
                        height: open ? height : "0px",
                        marginBottom: open ? "24px" : "0px",
                        marginTop: open ? "12px" : "0px",
                    }}
                    className="overflow-hidden"
                >
                    <div ref={ref}>
                        <FoldContent />
                    </div>
                </motion.div>
            )}
        </div>
    );
};

const MobileMenu = () => {
    const [open, setOpen] = useState(false);
    return (
        <div className="block lg:hidden">
            <button onClick={() => setOpen(true)} className="block text-3xl" title="open">
                <FiMenu />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ x: "100vw" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100vw" }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        className="fixed left-0 top-0 flex h-screen w-full flex-col bg-white"
                    >
                        <div className="flex items-center justify-between p-6">
                            <Image src="/footer-logo.svg" alt="Conceev Digital" width={200} height={50} />
                            
                            <button onClick={() => setOpen(false)} title="open">
                                <FiX className="text-3xl text-neutral-950" />
                            </button>
                        </div>
                        <div className="h-screen overflow-y-scroll bg-neutral-100 p-6">
                            {LINKS.map((l) => (
                                <MobileMenuLink
                                    key={l.text}
                                    href={l.href}
                                    FoldContent={l.component}
                                    setMenuOpen={setOpen}
                                >
                                    {l.text}
                                </MobileMenuLink>
                            ))}
                        </div>
                        <div className="flex justify-end bg-neutral-950 p-6">
                            <CTAs />
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </div>
    );
};

const LINKS = [
    {
        text: "Home",
        href: "/",
    },
    {
        text: "About us",
        href: "/about",
        // component: AboutUsContent,
    },
    {
        text: "Services",
        href: "#",
        component: ServicesContent,
    },
    {
        text: "Products",
        href: "#",
        component: ProductsContent,
    },
];